import React, { useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import { Download, Loader2, Search, Trash2, X } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import type {
  Event,
  EventRegistration,
  RegistrationMemberType,
  RegistrationPaymentStatus,
} from '../../types';
import { cn } from '../../lib/utils';
import { useApiJson } from '../../hooks/useApi';
import {
  deleteRegistration,
  downloadRegistrationsExport,
  eventRegistrationsPath,
  fetchRegistrationScreenshot,
  updateRegistrationPaymentStatus,
  type EventRegistrationsResponse,
} from '../../lib/events-api';
import {
  ageOn,
  firstHttpUrl,
  formatRupees,
  noteSegments,
  PAYMENT_STATUS_LABELS,
  registrationNotesHeading,
  adminRegistrationStateLabel,
} from '../../lib/event-utils';
import { filterRegistrations, parseRegistrationSort, sortRegistrations } from '../../lib/registration-list';
import { phoneLinks } from '../../lib/phone';
import { CANCEL_PAYMENT_BUTTON_LABEL, cancelPaymentConfirm } from '../../lib/payment-status';
import { registrationDeletedToast, pushScoringToast } from '../common/admin-toasts';
import { usePinDelete } from '../common/PinDeleteDialog';
import { WhatsappReminderTools } from './WhatsappReminderTools';

const PAYMENT_STATUSES: RegistrationPaymentStatus[] = ['pending', 'verified', 'rejected', 'not_required'];

const proofClass =
  'inline-flex items-center min-h-9 px-2 text-sm font-medium text-[var(--color-accent)] underline underline-offset-2 whitespace-nowrap';

const STATUS_STYLES: Record<RegistrationPaymentStatus, string> = {
  pending: 'bg-[var(--color-warning-subtle)] text-[var(--color-text-primary)]',
  verified: 'bg-[var(--color-success-subtle)] text-[var(--color-success-text)]',
  rejected: 'bg-[var(--color-error-subtle)] text-[var(--color-error-text)]',
  not_required: 'bg-[var(--color-surface-2)] text-[var(--color-text-secondary)]',
};

function isPaymentStatus(value: string | null): value is RegistrationPaymentStatus {
  return value != null && (PAYMENT_STATUSES as string[]).includes(value);
}

function isMemberType(value: string | null): value is RegistrationMemberType {
  return value === 'member' || value === 'non_member';
}

function ageAsOnDate(event: Event): string {
  if (event.formOptions.ageAsOn) return event.formOptions.ageAsOn;
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Kolkata',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date(event.date));
}

function memberLabel(memberType: RegistrationMemberType | null): string {
  if (memberType === 'member') return 'Member';
  if (memberType === 'non_member') return 'Non-member';
  return '—';
}

function NoteCell({ notes }: { notes: string | null }) {
  const parts = noteSegments(notes);
  if (parts.length === 0) return <>—</>;
  return (
    <span className="whitespace-pre-line break-words">
      {parts.map((part, index) =>
        part.type === 'link' ? (
          <a
            key={index}
            href={part.value}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[var(--color-accent)] underline underline-offset-2"
          >
            {part.value}
          </a>
        ) : (
          <span key={index}>{part.value}</span>
        ),
      )}
    </span>
  );
}

/** Phone number as a tap-to-call link, with a small WhatsApp link (+91 added when missing). */
function PhoneCell({ phone, className }: { phone: string; className: string }) {
  const links = phoneLinks(phone);
  if (!links) return <p className={className}>{phone}</p>;
  return (
    <p className={cn(className, 'flex flex-wrap items-center gap-x-3')}>
      <a href={links.tel} className="underline-offset-2 hover:underline" aria-label={`Call ${phone}`}>
        {phone}
      </a>
      <a
        href={links.whatsapp}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex min-h-8 items-center text-xs font-medium text-[var(--color-accent)] underline underline-offset-2"
        aria-label={`WhatsApp ${phone}`}
      >
        WhatsApp
      </a>
    </p>
  );
}

export function ScreenshotViewer({
  event,
  registration,
  onClose,
  onSetStatus,
}: {
  event: Event;
  registration: EventRegistration;
  onClose: () => void;
  /** Same status update as the list's dropdown; omitted for read-only viewers. Resolves to an error message or null. */
  onSetStatus?: (status: RegistrationPaymentStatus) => Promise<string | null>;
}) {
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState<RegistrationPaymentStatus | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  const setStatus = async (status: RegistrationPaymentStatus) => {
    if (!onSetStatus || saving) return;
    if (status === 'rejected' && !confirm(cancelPaymentConfirm(registration.regNumber, registration.name))) return;
    setSaving(status);
    setSaveError(null);
    const failed = await onSetStatus(status);
    setSaving(null);
    if (failed) setSaveError(failed);
    else onClose();
  };

  useEffect(() => {
    let objectUrl: string | null = null;
    let cancelled = false;
    fetchRegistrationScreenshot(event.id, registration.id)
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Could not load screenshot');
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [event.id, registration.id]);

  useEffect(() => {
    const onKey = (ev: KeyboardEvent) => ev.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-[oklch(18%_0.01_55/0.6)] p-4"
      role="dialog"
      aria-modal="true"
      aria-label={`Payment screenshot for ${registration.regNumber}`}
      onClick={onClose}
    >
      <div
        className="bg-[var(--color-surface-1)] rounded-[var(--radius-lg)] shadow-[var(--shadow-xl)] max-w-lg w-full max-h-[90vh] flex flex-col"
        onClick={(ev) => ev.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-[var(--color-border)]">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-[var(--color-text-primary)] truncate">
              {registration.regNumber} · {registration.name}
            </p>
            <p className="text-xs text-[var(--color-text-tertiary)]">
              Amount due {formatRupees(registration.amountDue)}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-[var(--radius-sm)] text-[var(--color-text-tertiary)] hover:bg-[var(--color-surface-2)]"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="flex-1 overflow-auto p-4 flex items-center justify-center min-h-48">
          {error ? (
            <p className="text-sm text-[var(--color-error-text)]">{error}</p>
          ) : url ? (
            <img src={url} alt={`Payment screenshot for ${registration.regNumber}`} className="max-w-full h-auto" />
          ) : (
            <Loader2 className="w-5 h-5 animate-spin text-[var(--color-text-tertiary)]" />
          )}
        </div>
        {onSetStatus ? (
          <div className="border-t border-[var(--color-border)] px-4 py-3 space-y-2">
            <p className="text-xs text-[var(--color-text-tertiary)]">
              Now: {PAYMENT_STATUS_LABELS[registration.paymentStatus]}
            </p>
            {saveError ? (
              <p className="text-sm text-[var(--color-error-text)]" role="alert">
                {saveError}
              </p>
            ) : null}
            <div className="flex flex-wrap justify-end gap-2">
              <button
                type="button"
                onClick={() => void setStatus('rejected')}
                disabled={saving !== null || registration.paymentStatus === 'rejected'}
                className="inline-flex min-h-11 items-center gap-2 rounded-[var(--radius-md)] border border-[var(--color-border-strong)] px-4 text-sm font-medium text-[var(--color-error-text)] disabled:opacity-50"
              >
                {saving === 'rejected' ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                {CANCEL_PAYMENT_BUTTON_LABEL}
              </button>
              <button
                type="button"
                onClick={() => void setStatus('verified')}
                disabled={saving !== null || registration.paymentStatus === 'verified'}
                className="inline-flex min-h-11 items-center gap-2 rounded-[var(--radius-md)] bg-[var(--color-accent)] px-4 text-sm font-semibold text-white disabled:opacity-50"
              >
                {saving === 'verified' ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                Mark verified
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function FilterChip({
  selected,
  onClick,
  children,
}: {
  selected: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-1.5 min-h-11 px-3 rounded-[var(--radius-full)] text-sm font-medium transition-colors',
        selected
          ? 'bg-[var(--color-accent-subtle)] text-[var(--color-accent-hover)]'
          : 'text-[var(--color-text-secondary)] hover:bg-[var(--color-surface-2)]',
      )}
    >
      {children}
    </button>
  );
}

function Count({ n }: { n: number }) {
  return <span className="tabular-nums opacity-70">{n}</span>;
}

function ProofLink({
  registration,
  onView,
  readOnly,
}: {
  registration: EventRegistration;
  onView: (registration: EventRegistration) => void;
  readOnly: boolean;
}) {
  if (registration.hasPaymentScreenshot) {
    if (readOnly) return <span className="text-sm text-[var(--color-text-tertiary)]">Uploaded</span>;
    return (
      <button
        type="button"
        onClick={() => onView(registration)}
        className={proofClass}
        title="View payment screenshot"
        aria-label={`View payment screenshot for ${registration.regNumber}`}
      >
        View
      </button>
    );
  }
  const href = firstHttpUrl(registration.notes);
  if (!href) return <span className="text-sm text-[var(--color-text-tertiary)]">—</span>;
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={proofClass}
      title="Open the payment proof saved with this registration"
    >
      View
    </a>
  );
}

export function EventRegistrationsPanel({
  event,
  events,
  onChanged,
  onSelectEvent,
  readOnly = false,
}: {
  event: Event;
  events: Event[];
  onChanged: () => Promise<void>;
  onSelectEvent: (eventId: string) => void;
  /** Organizers can read and export, not change payment status or open uploaded screenshots. */
  readOnly?: boolean;
}) {
  const { data, loading, error, mutate } = useApiJson<EventRegistrationsResponse>(
    eventRegistrationsPath(event.id),
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const [busyId, setBusyId] = useState<string | null>(null);
  const [removedIds, setRemovedIds] = useState<Set<string>>(new Set());
  const pinDelete = usePinDelete();
  const [actionError, setActionError] = useState<string | null>(null);
  const [viewing, setViewing] = useState<EventRegistration | null>(null);
  const [exporting, setExporting] = useState<'csv' | 'xlsx' | null>(null);

  const query = searchParams.get('q') ?? '';
  const statusFilter = isPaymentStatus(searchParams.get('paymentStatus')) ? searchParams.get('paymentStatus')! : '';
  const memberFilter = isMemberType(searchParams.get('memberType')) ? searchParams.get('memberType')! : '';
  const sort = parseRegistrationSort(searchParams.get('sort')) ?? 'newest';

  const setParam = (key: string, value: string) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value) next.set(key, value);
        else next.delete(key);
        return next;
      },
      { replace: true },
    );
  };

  const registrations = (data?.registrations ?? []).filter((row) => !removedIds.has(row.id));
  const showMemberType = event.formOptions.memberChoice || registrations.some((r) => r.memberType != null);
  const showTeam = event.formOptions.teamSize != null || registrations.some((r) => r.teammates.length > 0);
  const showRifleTeam = event.id === 'seed-evt-rifle-shooting-2026';
  const notesHeading = registrationNotesHeading(
    event.formOptions.notesQuestion,
    registrations.some((r) => Boolean(r.notes) && !firstHttpUrl(r.notes)),
  );
  const showDob = event.formOptions.collectDob || registrations.some((r) => Boolean(r.dob));
  const asOn = ageAsOnDate(event);
  const hasUploadedProof = registrations.some((r) => r.hasPaymentScreenshot);

  const searched = useMemo(() => filterRegistrations(registrations, { q: query }), [registrations, query]);
  const memberCounts = useMemo(
    () => ({
      member: searched.filter((r) => r.memberType === 'member').length,
      nonMember: searched.filter((r) => r.memberType === 'non_member').length,
    }),
    [searched],
  );
  const afterMember = useMemo(
    () => filterRegistrations(searched, { memberType: memberFilter || null }),
    [searched, memberFilter],
  );
  const statusCounts = useMemo(() => {
    const counts: Record<RegistrationPaymentStatus, number> = {
      pending: 0,
      verified: 0,
      rejected: 0,
      not_required: 0,
    };
    for (const row of afterMember) counts[row.paymentStatus] += 1;
    return counts;
  }, [afterMember]);
  const filtered = useMemo(
    () =>
      sortRegistrations(
        filterRegistrations(afterMember, { paymentStatus: statusFilter || null }),
        sort,
      ),
    [afterMember, statusFilter, sort],
  );

  const visibleStatuses = PAYMENT_STATUSES.filter(
    (status) => statusCounts[status] > 0 || (event.formOptions.paymentRequired && status !== 'not_required'),
  );

  const refresh = async () => {
    await mutate();
    await onChanged();
  };

  const handleStatus = async (reg: EventRegistration, status: RegistrationPaymentStatus) => {
    setBusyId(reg.id);
    setActionError(null);
    try {
      await updateRegistrationPaymentStatus(event.id, reg.id, status);
      await refresh();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Could not update payment status');
    } finally {
      setBusyId(null);
    }
  };

  /** The screenshot viewer's Mark verified / Cancel payment: same API call as the dropdown, error returned to the viewer. */
  const setStatusFromViewer = async (reg: EventRegistration, status: RegistrationPaymentStatus): Promise<string | null> => {
    setBusyId(reg.id);
    setActionError(null);
    try {
      await updateRegistrationPaymentStatus(event.id, reg.id, status);
      await refresh();
      return null;
    } catch (err) {
      return err instanceof Error ? err.message : 'Could not update payment status';
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = (reg: EventRegistration) => {
    pinDelete.ask({
      title: `Delete registration ${reg.regNumber} (${reg.name})?`,
      description: 'This cannot be undone.',
      confirmLabel: 'Delete registration',
      run: async (pin) => {
        await deleteRegistration(event.id, reg.id, pin);
        setRemovedIds((prev) => new Set(prev).add(reg.id));
        pushScoringToast(registrationDeletedToast(reg.regNumber, reg.name));
        void refresh();
      },
    });
  };

  const handleExport = async (fmt: 'csv' | 'xlsx') => {
    setExporting(fmt);
    setActionError(null);
    try {
      await downloadRegistrationsExport(event.id, fmt, event.slug || event.id, {
        q: query,
        paymentStatus: statusFilter || undefined,
        memberType: memberFilter || undefined,
        sort,
      });
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Export failed');
    } finally {
      setExporting(null);
    }
  };

  const clearFilters = () => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('q');
        next.delete('paymentStatus');
        next.delete('memberType');
        next.delete('sort');
        return next;
      },
      { replace: true },
    );
  };

  const filtersActive = Boolean(query || statusFilter || memberFilter || sort === 'oldest');
  const countLabel =
    filtered.length === registrations.length
      ? `${registrations.length} registration${registrations.length === 1 ? '' : 's'}`
      : `${filtered.length} of ${registrations.length} shown`;

  const selectClass =
    'bg-[var(--color-surface-1)] border border-[var(--color-border)] rounded-[var(--radius-md)] py-2 px-3 text-sm text-[var(--color-text-primary)] outline-none focus:border-[var(--color-accent)] min-h-11';

  const statusControl = (r: EventRegistration) =>
    readOnly ? (
      <span
        className={cn(
          'inline-flex items-center text-xs font-medium rounded-[var(--radius-sm)] py-1.5 px-2 min-h-9',
          STATUS_STYLES[r.paymentStatus],
        )}
      >
        {PAYMENT_STATUS_LABELS[r.paymentStatus]}
      </span>
    ) : (
    <select
      value={r.paymentStatus}
      disabled={busyId === r.id}
      onChange={(ev) => void handleStatus(r, ev.target.value as RegistrationPaymentStatus)}
      className={cn(
        'text-xs font-medium rounded-[var(--radius-sm)] py-1.5 px-2 border-0 outline-none focus:ring-2 focus:ring-[var(--color-accent-subtle)] min-h-9',
        STATUS_STYLES[r.paymentStatus],
      )}
      aria-label={`Payment status for ${r.regNumber}`}
    >
      {PAYMENT_STATUSES.map((s) => (
        <option key={s} value={s}>
          {PAYMENT_STATUS_LABELS[s]}
        </option>
      ))}
    </select>
  );

  const deleteControl = (r: EventRegistration) => readOnly ? null : (
    <button
      type="button"
      onClick={() => void handleDelete(r)}
      disabled={busyId === r.id}
      className="p-2 rounded-[var(--radius-sm)] text-[var(--color-text-tertiary)] hover:text-[var(--color-error)] hover:bg-[var(--color-error-subtle)]"
      title="Delete registration"
      aria-label={`Delete registration ${r.regNumber}`}
    >
      <Trash2 className="w-4 h-4" />
    </button>
  );

  const ageCell = (r: EventRegistration) => {
    if (!r.dob) return '—';
    const age = ageOn(r.dob, asOn);
    return (
      <span>
        <span className="tabular-nums text-[var(--color-text-primary)]">{age}</span>
        <span className="block text-[11px] text-[var(--color-text-tertiary)] tabular-nums">{r.dob}</span>
      </span>
    );
  };

  return (
    <div className="bg-[var(--color-surface-1)] rounded-[var(--radius-lg)] border border-[var(--color-border)]">
      {pinDelete.dialog}
      <div className="p-4 sm:p-5 border-b border-[var(--color-border)] space-y-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            {events.length > 1 ? (
              <label className="block max-w-md">
                <span className="sr-only">Event</span>
                <select
                  className={cn(selectClass, 'w-full font-semibold')}
                  value={event.id}
                  onChange={(ev) => onSelectEvent(ev.target.value)}
                  aria-label="Event"
                >
                  {events.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.title}
                    </option>
                  ))}
                </select>
              </label>
            ) : (
              <h3 className="text-base font-semibold text-[var(--color-text-primary)]">{event.title}</h3>
            )}
            <p className="text-xs text-[var(--color-text-secondary)] mt-1.5">
              {format(new Date(event.date), 'PPP p')} ·{' '}
              {adminRegistrationStateLabel(event)}
              {event.capacity != null && ` · ${event.remaining ?? 0} of ${event.capacity} places left`}
              {registrations.length > 0 && ` · ${countLabel}`}
            </p>
            {hasUploadedProof && !readOnly && (
              <p className="text-xs text-[var(--color-text-tertiary)] mt-1">
                Uploaded payment screenshots open only while you are signed in as an admin.
              </p>
            )}
          </div>
          <div className="flex gap-2">
            {(['csv', 'xlsx'] as const).map((fmt) => (
              <button
                key={fmt}
                type="button"
                onClick={() => void handleExport(fmt)}
                disabled={exporting !== null || filtered.length === 0}
                title="Exports the registrations currently shown"
                className="inline-flex items-center gap-1.5 min-h-11 px-3 py-2 rounded-[var(--radius-md)] text-sm font-medium border border-[var(--color-border)] text-[var(--color-text-secondary)] hover:bg-[var(--color-surface-2)] disabled:opacity-50"
              >
                {exporting === fmt ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                {fmt.toUpperCase()}
              </button>
            ))}
          </div>
        </div>

        {!readOnly && <WhatsappReminderTools eventId={event.id} />}

        <div className="space-y-2">
          <div className="flex flex-wrap gap-1" role="group" aria-label="Filter by payment status">
            <FilterChip selected={statusFilter === ''} onClick={() => setParam('paymentStatus', '')}>
              All <Count n={afterMember.length} />
            </FilterChip>
            {visibleStatuses.map((status) => (
              <div key={status} className="contents">
                <FilterChip
                  selected={statusFilter === status}
                  onClick={() => setParam('paymentStatus', statusFilter === status ? '' : status)}
                >
                  {PAYMENT_STATUS_LABELS[status]} <Count n={statusCounts[status]} />
                </FilterChip>
              </div>
            ))}
          </div>
          {showMemberType && (
            <div className="flex flex-wrap gap-1" role="group" aria-label="Filter by membership">
              <FilterChip selected={memberFilter === ''} onClick={() => setParam('memberType', '')}>
                Everyone <Count n={searched.length} />
              </FilterChip>
              <FilterChip
                selected={memberFilter === 'member'}
                onClick={() => setParam('memberType', memberFilter === 'member' ? '' : 'member')}
              >
                Members <Count n={memberCounts.member} />
              </FilterChip>
              <FilterChip
                selected={memberFilter === 'non_member'}
                onClick={() => setParam('memberType', memberFilter === 'non_member' ? '' : 'non_member')}
              >
                Non-members <Count n={memberCounts.nonMember} />
              </FilterChip>
            </div>
          )}
        </div>

        <div className="flex flex-wrap gap-2">
          <div className="relative flex-1 min-w-48">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--color-text-tertiary)]" />
            <input
              type="search"
              placeholder="Name, phone, or registration number"
              value={query}
              onChange={(ev) => setParam('q', ev.target.value)}
              aria-label="Search registrations"
              className="w-full bg-[var(--color-surface-1)] border border-[var(--color-border)] rounded-[var(--radius-md)] py-2 pl-9 pr-3 text-sm text-[var(--color-text-primary)] outline-none focus:border-[var(--color-accent)] min-h-11"
            />
          </div>
          <select
            className={selectClass}
            value={sort}
            onChange={(ev) => setParam('sort', ev.target.value === 'oldest' ? 'oldest' : '')}
            aria-label="Sort registrations"
          >
            <option value="newest">Newest first</option>
            <option value="oldest">Oldest first</option>
          </select>
          {filtersActive && (
            <button
              type="button"
              onClick={clearFilters}
              className="min-h-11 px-3 text-sm font-medium text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]"
            >
              Clear
            </button>
          )}
        </div>

        {actionError && (
          <p role="alert" className="text-sm text-[var(--color-error-text)] bg-[var(--color-error-subtle)] rounded-[var(--radius-md)] px-3 py-2">
            {actionError}
          </p>
        )}
      </div>

      {loading ? (
        <div className="p-10 flex justify-center">
          <Loader2 className="w-5 h-5 animate-spin text-[var(--color-text-tertiary)]" />
        </div>
      ) : error ? (
        <p className="p-6 text-sm text-[var(--color-error-text)]">{error.message}</p>
      ) : filtered.length === 0 ? (
        <p className="p-10 text-sm text-center text-[var(--color-text-tertiary)]">
          {registrations.length === 0
            ? 'No registrations yet. They appear here as soon as someone signs up on the public site or in the app.'
            : 'No registrations match these filters.'}
        </p>
      ) : (
        <>
          <ul className="md:hidden divide-y divide-[var(--color-border)]">
            {filtered.map((r) => (
              <li key={r.id} className="px-4 py-3 space-y-2">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-medium text-[var(--color-text-primary)]">{r.name}</p>
                    <p className="text-xs font-mono text-[var(--color-text-secondary)] mt-0.5">{r.regNumber}</p>
                    {showRifleTeam ? <p className="text-xs text-[var(--color-text-secondary)]">{r.teamName ?? 'No team'}</p> : null}
                  </div>
                  <p className="text-xs text-[var(--color-text-secondary)] whitespace-nowrap">
                    {format(new Date(r.createdAt), 'd MMM, p')}
                  </p>
                </div>
                <PhoneCell phone={r.phone} className="text-sm tabular-nums text-[var(--color-text-primary)]" />
                {r.email && <p className="text-xs text-[var(--color-text-tertiary)] break-all">{r.email}</p>}
                <p className="text-xs text-[var(--color-text-secondary)]">
                  {showMemberType && (
                    <span>
                      {memberLabel(r.memberType)}
                      {r.lmNumber ? ` · ${r.lmNumber}` : ''}
                      {' · '}
                    </span>
                  )}
                  {showDob && r.dob && <span>Age {ageOn(r.dob, asOn)} · </span>}
                  {formatRupees(r.amountDue)}
                </p>
                {showTeam && r.teammates.length > 0 && (
                  <p className="text-xs text-[var(--color-text-secondary)]">{r.teammates.join(', ')}</p>
                )}
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    {statusControl(r)}
                    <ProofLink registration={r} onView={setViewing} readOnly={readOnly} />
                  </div>
                  {deleteControl(r)}
                </div>
              </li>
            ))}
          </ul>

          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-[var(--color-text-tertiary)] border-b border-[var(--color-border)]">
                  <th className="font-medium px-4 py-2.5">Reg no.</th>
                  <th className="font-medium px-4 py-2.5">Registrant</th>
                  {showRifleTeam ? <th className="font-medium px-4 py-2.5">Team</th> : null}
                  {showDob && <th className="font-medium px-4 py-2.5">Age</th>}
                  {showMemberType && <th className="font-medium px-4 py-2.5">Member</th>}
                  {showTeam && <th className="font-medium px-4 py-2.5">Teammates</th>}
                  {notesHeading && <th className="font-medium px-4 py-2.5">{notesHeading}</th>}
                  <th className="font-medium px-4 py-2.5">Amount</th>
                  <th className="font-medium px-4 py-2.5">Payment</th>
                  <th className="font-medium px-4 py-2.5">Proof</th>
                  <th className="font-medium px-4 py-2.5">Registered</th>
                  <th className="px-4 py-2.5">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.id} className="border-b border-[var(--color-border)] last:border-b-0 align-top">
                    <td className="px-4 py-3 font-mono text-xs text-[var(--color-text-primary)] whitespace-nowrap">
                      {r.regNumber}
                    </td>
                    <td className="px-4 py-3 min-w-48">
                      <p className="font-medium text-[var(--color-text-primary)]">{r.name}</p>
                      <PhoneCell phone={r.phone} className="text-xs text-[var(--color-text-secondary)] tabular-nums" />
                      {r.email && <p className="text-xs text-[var(--color-text-tertiary)] break-all">{r.email}</p>}
                      {r.gender && <p className="text-xs text-[var(--color-text-tertiary)] capitalize">{r.gender}</p>}
                    </td>
                    {showRifleTeam ? <td className="px-4 py-3 text-xs">{r.teamName ?? '—'}</td> : null}
                    {showDob && <td className="px-4 py-3 text-xs whitespace-nowrap">{ageCell(r)}</td>}
                    {showMemberType && (
                      <td className="px-4 py-3 text-xs whitespace-nowrap">
                        {memberLabel(r.memberType)}
                        {r.lmNumber && (
                          <span className="block text-[var(--color-text-tertiary)] mt-0.5">{r.lmNumber}</span>
                        )}
                      </td>
                    )}
                    {showTeam && <td className="px-4 py-3 text-xs min-w-40">{r.teammates.join(', ') || '—'}</td>}
                    {notesHeading && (
                      <td className="px-4 py-3 text-xs min-w-40 max-w-64">
                        <NoteCell notes={r.notes} />
                      </td>
                    )}
                    <td className="px-4 py-3 text-xs whitespace-nowrap tabular-nums">{formatRupees(r.amountDue)}</td>
                    <td className="px-4 py-3">{statusControl(r)}</td>
                    <td className="px-4 py-3">
                      <ProofLink registration={r} onView={setViewing} readOnly={readOnly} />
                    </td>
                    <td className="px-4 py-3 text-xs text-[var(--color-text-secondary)] whitespace-nowrap">
                      {format(new Date(r.createdAt), 'd MMM, p')}
                    </td>
                    <td className="px-4 py-3">{deleteControl(r)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {viewing && (
        <ScreenshotViewer
          event={event}
          registration={viewing}
          onClose={() => setViewing(null)}
          onSetStatus={readOnly ? undefined : (status) => setStatusFromViewer(viewing, status)}
        />
      )}
    </div>
  );
}
